/**
 * Floating Mock / Live data-source toggle.
 *
 * A segmented switch: click "Mock" or "Live" to flip the app's data source. The
 * choice is persisted (localStorage) and wins over the deployment's per-domain
 * config, so it actually changes what you see (see `@/config/runtime`).
 */

import { Database, Cloud } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { getApiMode, setApiMode } from '@/config/runtime'

export default function ApiModeIndicator() {
  const { pathname } = useLocation()
  const isMock = getApiMode() === 'mock'
  if (pathname.startsWith('/independent-account/') || pathname === '/firm-registration') return null

  return (
    <div className="fixed bottom-4 left-4 z-50">
      <div
        role="group"
        aria-label="API data source"
        className="inline-flex items-center gap-0.5 rounded-full border border-surface-overlay bg-surface-elevated p-0.5 shadow-lg"
      >
        <button
          type="button"
          onClick={() => isMock || setApiMode('mock')}
          aria-pressed={isMock}
          title="Use in-memory mock data"
          className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition ${
            isMock
              ? 'bg-gray-500 text-white shadow dark:bg-gray-600'
              : 'text-text-secondary hover:text-text-primary'
          }`}
        >
          <Database size={13} aria-hidden="true" />
          Mock
        </button>
        <button
          type="button"
          onClick={() => isMock && setApiMode('real')}
          aria-pressed={!isMock}
          title="Use the live API"
          className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition ${
            !isMock
              ? 'bg-primary-600 text-white shadow'
              : 'text-text-secondary hover:text-text-primary'
          }`}
        >
          <Cloud size={13} aria-hidden="true" />
          Live
        </button>
      </div>
    </div>
  )
}
